import {Subscription} from "rxjs";
import {DatePipe} from "@angular/common";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {ToastrService} from "ngx-toastr";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {
  defaultPageLimit,
  ImageComponent,
  PaginationComponent,
  SearchInputComponent,
  SearchResultComponent,
  SortOption
} from "@numizmat/components";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {ConversationMessageModalComponent} from "@numizmat/modals";
import {RestoreScrollPositionDirective} from "@numizmat/directives";
import {AlertService, ModalService} from "@numizmat/services/common";
import {ProfileConversationApiService} from '@numizmat/services/core';
import {
  ProfileConversationSortOptionApiService,
  ProfileConversationSortOptionStateService
} from '@numizmat/services/nomenclature';
import {
  FilterProfileConversationResponse,
  FilterProfileConversationsRequest,
  FilterProfileConversationsResponse
} from "@numizmat/services-models/core";
import {
  ProfileMessagesGroupActionsModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/profile-messages-group-actions-modal/profile-messages-group-actions-modal.component";

@Component({
  selector: 'nz-profile-messages',
  imports: [
    DatePipe,
    RouterLink,
    ImageComponent,
    PaginationComponent,
    SearchInputComponent,
    SearchResultComponent,
    RestoreScrollPositionDirective
  ],
  templateUrl: './profile-messages.component.html',
  styleUrl: './profile-messages.component.scss'
})
export class ProfileMessagesComponent implements OnInit, OnDestroy {
  @Input() targetMenuId!: string;

  private router: Router = inject(Router);
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private toastrService: ToastrService = inject(ToastrService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private profileConversationApiService: ProfileConversationApiService = inject(ProfileConversationApiService);
  private profileConversationSortOptionApiService: ProfileConversationSortOptionApiService = inject(ProfileConversationSortOptionApiService);
  private profileConversationSortOptionStateService: ProfileConversationSortOptionStateService = inject(ProfileConversationSortOptionStateService);

  private currentRoute: string = '';
  private subs: Subscription = new Subscription();

  protected sortOptions: SortOption[] = [];
  protected selectedSortOption: number = 1;
  protected loading: boolean = true;
  protected totalResults: number = 0;
  protected searchValue: string = '';
  protected selectedPage: number = 1;
  protected selectedPageLimitOptionId: number = defaultPageLimit;
  protected conversations: FilterProfileConversationResponse[] = [];

  protected readonly appRoutes = appRoutes;
  protected readonly environment = environment;
  protected readonly defaultPageLimit = defaultPageLimit;

  public ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0];
    this.getAllProfileConversationSortOptions();
    this.createQueryParamsChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onSort(sortOption: number): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {sortOption: sortOption, page: 1},
    }).then();
  }

  protected onSearch(searchValue: string): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {searchValue: searchValue, page: 1},
    }).then();
  }

  protected onOpenConversationMessageModal(conversation: FilterProfileConversationResponse): void {
    const index: number = this.conversations.findIndex(x => x.conversationUserId === conversation.conversationUserId);
    this.conversations[index].isLastMessageRead = true;
    const modalRef: NgbModalRef = this.modalService.open(ConversationMessageModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.mode = conversation.isUserAdOwner ? 'seller' : 'buyer';
    modalRef.componentInstance.adId = conversation.adId;
    modalRef.componentInstance.adTitle = conversation.adTitle;
    modalRef.componentInstance.adPrice = conversation.adPrice;
    modalRef.componentInstance.interlocutorUsername = conversation.interlocutorUsername;
    const subscription: Subscription = modalRef.componentInstance.onSubmitMessage.subscribe({
      next: (message: string) => {
        this.conversations[index].lastMessageDateTime = new Date();
      }
    });
    this.subs.add(subscription);
  }

  protected onOpenProfileMessagesGroupActionsModal(mode: 'delete'): void {
    const modalRef: NgbModalRef = this.modalService.open(ProfileMessagesGroupActionsModalComponent, {
      size: 'lg',
      scrollable: true,
      modalDialogClass: 'full-height-modal'
    });
    modalRef.componentInstance.mode = mode;
    const subscription: Subscription = modalRef.componentInstance.reloadData.subscribe({
      next: (success: boolean) => {
        if (success) {
          this.filterProfileConversations();
        }
      }
    });
    this.subs.add(subscription)
  }

  protected showDeleteMessageWarning(conversation: FilterProfileConversationResponse): void {
    this.alertService.showDeleteMessageWarning(conversation.interlocutorUsername, conversation.adTitle).then((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.totalResults = 0;
        this.profileConversationApiService.deleteProfileConversation(conversation.conversationUserId).subscribe({
          next: response => {
            this.filterProfileConversations();
            this.toastrService.success('Успешно изтрихте съобщение.')
          }
        });
      }
    });
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        const menuId = queryParams['menuId'];
        if (menuId !== this.targetMenuId) {
          return;
        }
        this.searchValue = queryParams['searchValue'];
        this.selectedPage = !!queryParams['page'] ? Number.parseInt(queryParams['page']) : 1;
        this.selectedPageLimitOptionId = !!queryParams['pageLimit'] ? Number.parseInt(queryParams['pageLimit']) : defaultPageLimit;
        this.selectedSortOption = !!queryParams['sortOption'] ? Number.parseInt(queryParams['sortOption']) : 1;
        this.filterProfileConversations();
      }
    });
    this.subs.add(subscription);
  }

  private filterProfileConversations(): void {
    this.loading = true;
    const request: FilterProfileConversationsRequest = {
      searchValue: this.searchValue,
      selectedPage: this.selectedPage,
      selectedPageLimit: this.selectedPageLimitOptionId,
      selectedSortOption: this.selectedSortOption,
    };
    this.profileConversationApiService.filterProfileConversations(request).subscribe({
      next: (response: FilterProfileConversationsResponse) => {
        this.totalResults = response.totalResults;
        this.conversations = response.conversations;
        this.loading = false;
      }
    });
  }

  private getAllProfileConversationSortOptions() {
    const subscription: Subscription = this.profileConversationSortOptionStateService.getAllProfileConversationSortOptions().subscribe({
      next: conversationSortOptions => {
        if (!!conversationSortOptions) {
          this.sortOptions = conversationSortOptions.map(x => {
            return {
              id: x.profileConversationSortOption,
              name: x.profileConversationSortOptionName
            }
          });
        } else {
          this.profileConversationSortOptionApiService.getAllProfileConversationSortOptions().subscribe({
            next: response => {
              this.profileConversationSortOptionStateService.setAllProfileConversationSortOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }
}
